import styles from "./StepThree.module.css";
import Icon from "../../../components/Icon/Icon";
import { useEffect, useState, useMemo } from "react";

import CreatePropertyModal from "../../../components/CreatePropertyModal/CreatePropertyModal";
import EditPropertyModal from "../../../components/EditPropertyModal/EditPropertyModal";
import type {
  GetTemplateResultSuccessType,
  IExtractDataFormElement,
} from "../../../shared/types/files.types";
import type {
  ICreateTemplateCredentials,
  ICreateTemplateFieldsCredentials,
} from "../../../shared/types/templates.types";
import Tooltip from "../../../components/Tooltip/Tooltip";

type StepThreeProps = {
  onSubmit: (fields: ICreateTemplateCredentials["fields"]) => void;
  extractedData: GetTemplateResultSuccessType;
  getBack: () => void;
  onTestQueries: (queries: { field: string; question: string }[]) => void;
};

type FieldType = ICreateTemplateFieldsCredentials & {
  sampleValue: string;
  confidence: number;
  isPredefined?: boolean;
  queries: { question: string; alias?: string }[];
};
type FieldsType = FieldType[];

const toCamelCase = (label: string) => {
  return label
    .trim()
    .split(" ")
    .map((word, index) =>
      index === 0
        ? word.toLowerCase()
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .join("");
};

const PREDEFINED_FIELDS: FieldsType = [
  {
    name: "name",
    label: "Name",
    keywords: [{ keyword: "nombre" }],
    queries: [],
    sampleValue: "Valor no encontrado",
    confidence: 0,
    isRequired: true,
    isPredefined: true,
    order: 0,
  },
  {
    name: "adress",
    label: "Adress",
    keywords: [{ keyword: "dirección" }, { keyword: "domicilio" }],
    queries: [],
    sampleValue: "Valor no encontrado",
    confidence: 0,
    isRequired: true,
    isPredefined: true,
    order: 0,
  },
  {
    name: "date",
    label: "Date",
    keywords: [{ keyword: "fecha" }],
    queries: [],
    sampleValue: "Valor no encontrado",
    confidence: 0,
    isRequired: true,
    isPredefined: true,
    order: 0,
  },
  {
    name: "id",
    label: "ID",
    keywords: [{ keyword: "identidad" }, { keyword: "identificación" }],
    queries: [],
    sampleValue: "Valor no encontrado",
    confidence: 0,
    isRequired: true,
    isPredefined: true,
    order: 0,
  },
];

const StepThree = ({
  onSubmit,
  extractedData,
  getBack,
  onTestQueries,
}: StepThreeProps) => {
  const uniqueExtractedForms = useMemo(() => {
    const formMap = new Map<string, IExtractDataFormElement>();
    extractedData.forms.forEach((form) => {
      const baseKeyword = form.key.replace(/:.*$/, "").toLowerCase();
      const existingForm = formMap.get(baseKeyword);

      if (!existingForm || form.confidence > existingForm.confidence) {
        formMap.set(baseKeyword, form);
      }
    });
    return Array.from(formMap.values());
  }, [extractedData.forms]);

  const [typeFields, setTypeFields] = useState<FieldsType>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [fieldToEdit, setFieldToEdit] = useState<FieldType | null>(null);

  // render predefined & set unique
  useEffect(() => {
    const initialFields: FieldsType = [];

    PREDEFINED_FIELDS.forEach((predefinedField) => {
      const matchedForm = uniqueExtractedForms.find(
        (form) =>
          predefinedField.keywords &&
          predefinedField.keywords.some((keywordObj) =>
            form.key.toLowerCase().includes(keywordObj.keyword.toLowerCase())
          )
      );

      if (matchedForm) {
        initialFields.push({
          ...predefinedField,
          name: toCamelCase(predefinedField.name),
          sampleValue:
            matchedForm.sampleValue === ""
              ? "Valor no encontrado"
              : matchedForm.sampleValue,
          confidence: Math.floor(matchedForm.confidence),
          order: initialFields.length + 1,
        });
      } else {
        initialFields.push({
          ...predefinedField,
          order: initialFields.length + 1,
        });
      }
    });

    setTypeFields(initialFields);
  }, [uniqueExtractedForms]);

  // --- Update all fields' sampleValue after entering Step 3 or when keywords/extracted data change ---
  useEffect(() => {
    setTypeFields((prevFields) =>
      prevFields.map((field) => {
        let matchedSampleValue = "Valor no encontrado";
        let matchedConfidence = 0;
        // Find a match for any keyword
        const foundMatch =
          field.keywords && field.keywords.length > 0
            ? uniqueExtractedForms.find(
                (form) =>
                  field.keywords &&
                  field.keywords.some((keywordObj) =>
                    form.key
                      .toLowerCase()
                      .includes(keywordObj.keyword.toLowerCase())
                  )
              )
            : undefined;
        if (foundMatch) {
          matchedSampleValue =
            foundMatch.sampleValue === ""
              ? "Valor no encontrado"
              : foundMatch.sampleValue;
          matchedConfidence = Math.floor(foundMatch.confidence);
        }
        return {
          ...field,
          sampleValue: matchedSampleValue,
          confidence: matchedConfidence,
        };
      })
    );
  }, [uniqueExtractedForms]);

  const handleCreateProperty = ({
    label,
    keywords,
    queries,
  }: {
    label: string;
    keywords: { keyword: string }[];
    queries: { question: string; alias?: string }[];
  }) => {
    let matchedSampleValue = "Valor no encontrado";
    let matchedConfidence = 0;
    const foundMatch = uniqueExtractedForms.find((form) =>
      keywords.some((keywordObj) =>
        form.key.toLowerCase().includes(keywordObj.keyword.toLowerCase())
      )
    );
    if (foundMatch) {
      matchedSampleValue =
        foundMatch.sampleValue === ""
          ? "Valor no encontrado"
          : foundMatch.sampleValue;
      matchedConfidence = Math.floor(foundMatch.confidence);
    }
    setTypeFields((prev) => [
      {
        name: toCamelCase(label),
        label: label,
        keywords,
        queries: queries || [],
        isRequired: true,
        order: prev.length + 1,
        sampleValue: matchedSampleValue,
        confidence: matchedConfidence,
      },
      ...prev,
    ]);
    setIsCreateModalOpen(false);
  };

  const handleEditProperty = (updatedField: FieldType) => {
    const newName = toCamelCase(updatedField.label);

    let updatedSampleValue = "Valor no encontrado";
    let updatedConfidence = 0;
    const foundMatch = uniqueExtractedForms.find(
      (form) =>
        updatedField.keywords &&
        updatedField.keywords.some((keywordObj) =>
          form.key.toLowerCase().includes(keywordObj.keyword.toLowerCase())
        )
    );
    if (foundMatch) {
      updatedSampleValue =
        foundMatch.sampleValue === ""
          ? "Valor no encontrado"
          : foundMatch.sampleValue;
      updatedConfidence = Math.floor(foundMatch.confidence);
    }

    setTypeFields((prevFields) =>
      prevFields.map((field) => {
        if (field.name === updatedField.name) {
          return {
            ...updatedField,
            name: newName,
            sampleValue: updatedSampleValue,
            confidence: updatedConfidence,
          };
        }
        return field;
      })
    );
    setIsEditModalOpen(false);
    setFieldToEdit(null);
  };

  const [selectedFields, setSelectedFields] = useState<string[]>([]);

  const handleCheckboxChange = (key: string) => {
    setSelectedFields((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleDeleteSelected = () => {
    setTypeFields((prev) =>
      prev.filter((field) => !selectedFields.includes(field.name))
    );
    setSelectedFields([]);
  };

  const existingFieldsNamesForValidation = useMemo(() => {
    if (isEditModalOpen && fieldToEdit) {
      return typeFields
        .filter((field) => field.name !== fieldToEdit.name)
        .map((field) => field.label);
    }
    return typeFields.map((field) => field.label);
  }, [typeFields, isEditModalOpen, fieldToEdit]);

  const existingKeywordsGlobal = uniqueExtractedForms.flatMap((form) =>
    form.key.replace(/:.*$/, "").toLowerCase()
  );

  const handleSumbit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (typeFields.length === 0) {
      alert("¡No se puede crear un tipo sin propiedades!");
      return;
    }
    const fieldsToSend: ICreateTemplateFieldsCredentials[] = typeFields.map(
      (field, index) => {
        return {
          name: field.name,
          label: field.label,
          order: index + 1,
          isRequired: true,
          keywords: field.keywords,
          queries: field.queries || [],
        };
      }
    );
    onSubmit(fieldsToSend);
  };

  // --- Test Queries Button Logic ---
  const handleTestQueries = () => {
    const queries = typeFields.flatMap((field) =>
      field.queries.map((query) => ({
        field: field.name,
        question: query.question,
      }))
    );
    onTestQueries(queries);
  };

  return (
    <>
      <form className={styles.form} onSubmit={handleSumbit}>
        <div className={styles.container}>
          <h2 className={styles.title}>Propiedades extraídos</h2>
          <div className={styles.buttonsContainer}>
            <button
              className={styles.createButton}
              type="button"
              onClick={() => setIsCreateModalOpen(true)}
            >
              Crear propiedad
              <Icon size={22} name="edit-pen" gradient={true} />
            </button>
            <span className={styles.divider}></span>
            <button
              onClick={handleDeleteSelected}
              className={styles.deleteButton}
              type="button"
              disabled={selectedFields.length === 0}
            >
              Eliminar propiedades
              <Icon name="trash-can" size={18} color="#FFFFFF" />
            </button>
          </div>
          <ul className={styles.propertiesList}>
            {typeFields.map((field) => (
              <li className={styles.propertiesListItem} key={field.name}>
                <div className={styles.propertiesActionsContainer}>
                  {field.isPredefined && (
                    <Tooltip
                      content="Esta es una propiedad predeterminada del 
                      sistema con palabras clave predeterminadas. Si no se
                      encuentra el valor, es normal. Intente cambiar las palabras
                      clave de este campo para obtener el resultado deseado."
                      wrapperClassName={styles.tooltipWrapper}
                    >
                      <p className={styles.tooltipContent}>i</p>
                    </Tooltip>
                  )}
                  <button
                    className={styles.editFieldButton}
                    type="button"
                    onClick={() => {
                      setFieldToEdit(field);
                      setIsEditModalOpen(true);
                    }}
                  >
                    <Icon name="edit-pen" size={24} />
                  </button>
                </div>
                <div className={`${styles.propertyContent} `}>
                  <p className={styles.propertyName} title={field.label}>
                    {field.label}
                  </p>

                  {/* Show keywords if any, with sample value in parentheses only for user-inputted keywords */}
                  {field.keywords && field.keywords.length > 0 && (
                    <ul className={styles.keywordsList}>
                      {field.keywords.map((keywordObj, i) => {
                        return (
                          <li
                            key={i}
                            className={styles.keywordsListItem}
                            title={keywordObj.keyword}
                          >
                            <span className={styles.keywordTextEllipsis}>
                              {keywordObj.keyword}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  {/* Show queries if any, styled like EditType */}
                  {field.queries && field.queries.length > 0 && (
                    <ul
                      className={styles.keywordsList}
                      style={{ marginTop: 4 }}
                    >
                      {field.queries.map((q, i) => (
                        <li key={i} className={styles.keywordsListItem}>
                          <span>{q.question}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className={styles.inputContainer}>
                    <input
                      className={styles.customCheckbox}
                      onChange={() => handleCheckboxChange(field.name)}
                      checked={selectedFields.includes(field.name)}
                      type="checkbox"
                    />
                    <input
                      className={styles.propertyData}
                      value={field.sampleValue}
                      readOnly
                    />
                    <span className={styles.confidence}>
                      {`(${Math.floor(field.confidence)}% precisión)`}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className={styles.formActionsButtonsContainer}>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={getBack}
          >
            Volver
          </button>
          {/* --- Add Test Queries Button --- */}
          <button
            type="button"
            className={styles.submitButton}
            style={{ marginRight: 8 }}
            onClick={handleTestQueries}
          >
            Test Queries
          </button>
          <button className={styles.submitButton} type="submit">
            Crear Documento
          </button>
        </div>
      </form>

      <CreatePropertyModal
        isOpen={isCreateModalOpen}
        onRequestClose={() => setIsCreateModalOpen(false)}
        existingFieldsNames={existingFieldsNamesForValidation}
        existingKeywords={existingKeywordsGlobal}
        onCreate={handleCreateProperty}
      />

      {fieldToEdit && (
        <EditPropertyModal
          isOpen={isEditModalOpen}
          onRequestClose={() => {
            setIsEditModalOpen(false);
            setFieldToEdit(null);
          }}
          existingFieldsNames={existingFieldsNamesForValidation}
          existingKeywords={existingKeywordsGlobal}
          fieldToEdit={{
            ...fieldToEdit,
            keywords:
              fieldToEdit.keywords && Array.isArray(fieldToEdit.keywords)
                ? fieldToEdit.keywords.map((k) => ({ keyword: k.keyword }))
                : [],
            queries:
              fieldToEdit.queries && Array.isArray(fieldToEdit.queries)
                ? fieldToEdit.queries.map((q) => ({ question: q.question }))
                : [],
          }}
          onEdit={handleEditProperty}
        />
      )}
    </>
  );
};

export default StepThree;
